import { useState } from "react";
import { api, friendlyError, logIssue, adminMediaUrl } from "@/lib/api";
import { useMediaLibrary, MediaUploader, MediaSearchBar } from "@/components/admin/Media";
import { Toast } from "@/components/admin/Editors";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Save, Trash2, RefreshCw, Crosshair, Lock, Globe } from "lucide-react";

const VIS = [
  ["public", "Website-Medien", Globe],
  ["private", "Private Kundenfotos", Lock],
];

export default function MediaManager() {
  const [visibility, setVisibility] = useState("public");
  const { items, error, load, q, setQ, ctype, setCtype } = useMediaLibrary(visibility);
  const [edited, setEdited] = useState({});
  const [usage, setUsage] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 2200);
  };

  const patch = async (m) => {
    setErr("");
    try {
      await api.patch(`/admin/media/${m.id}`, {
        title: edited[m.id]?.title ?? m.title,
        alt: edited[m.id]?.alt ?? m.alt,
        focal_x: edited[m.id]?.focal_x ?? m.focal_x,
        focal_y: edited[m.id]?.focal_y ?? m.focal_y,
      });
      flash("Gespeichert");
      load();
    } catch (e) {
      logIssue("PATCH /admin/media", e);
      setErr(friendlyError(e));
    }
  };

  const replace = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setErr("");
    try {
      await api.post(`/admin/media/${id}/replace`, fd, { timeout: 180000 });
      flash("Bild ersetzt");
      load();
    } catch (e) {
      logIssue("POST replace", e);
      setErr(friendlyError(e));
    }
  };

  const showUsage = async (id) => {
    try {
      const { data } = await api.get(`/admin/media/${id}/usage`);
      setUsage((u) => ({ ...u, [id]: data }));
    } catch (e) {
      logIssue("GET usage", e);
    }
  };

  const remove = async () => {
    const id = confirm;
    setConfirm(null);
    setErr("");
    try {
      await api.delete(`/admin/media/${id}`);
      flash("Gelöscht");
      load();
    } catch (e) {
      logIssue("DELETE /admin/media", e);
      setErr(friendlyError(e));
    }
  };

  const setField = (id, key, value) =>
    setEdited((e) => ({ ...e, [id]: { ...e[id], [key]: value } }));

  return (
    <div data-testid="admin-media">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="ap-h3 text-ink">Medien</h2>
        <div className="flex gap-2">
          {VIS.map(([v, label, Icon]) => (
            <button
              key={v}
              type="button"
              onClick={() => setVisibility(v)}
              aria-pressed={visibility === v}
              data-testid={`media-visibility-${v}`}
              className={`flex min-h-[44px] items-center gap-2 border px-4 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${
                visibility === v ? "border-ink bg-ink text-paper" : "border-paper-line bg-paper text-charcoal"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      {visibility === "public" ? (
        <MediaUploader onUploaded={() => { load(); flash("Bild hochgeladen"); }} visibility="public" />
      ) : (
        <p className="ap-body border-l-2 border-signal pl-4 text-charcoal" data-testid="media-private-note">
          Private Kundenfotos und Referenzbilder aus Anfragen liegen in einem getrennten, geschützten
          Speicherbereich. Sie sind nie öffentlich abrufbar, tragen noindex und werden hier nur zur
          Kontrolle angezeigt.
        </p>
      )}

      <div className="mt-6">
        <MediaSearchBar q={q} setQ={setQ} ctype={ctype} setCtype={setCtype} />
      </div>

      {error || err ? (
        <p role="alert" data-testid="media-error" className="mt-4 border border-destructive bg-destructive/5 p-3 text-sm text-destructive">
          {err || error}
        </p>
      ) : null}

      {items === null ? (
        <Loader2 className="mt-8 animate-spin text-charcoal" />
      ) : items.length === 0 ? (
        <p className="ap-body mt-8 text-charcoal" data-testid="media-empty">
          Noch keine Bilder in diesem Bereich.
        </p>
      ) : (
        <div className="mt-6 space-y-5">
          {items.map((m, i) => {
            const e = edited[m.id] || {};
            const fx = e.focal_x ?? m.focal_x;
            const fy = e.focal_y ?? m.focal_y;
            return (
              <article key={m.id} data-testid={`media-item-${i}`} className="border border-paper-line bg-paper p-5">
                <div className="grid gap-5 sm:grid-cols-[220px_1fr]">
                  <div>
                    <button
                      type="button"
                      title="Fokuspunkt setzen"
                      onClick={(ev) => {
                        const r = ev.currentTarget.getBoundingClientRect();
                        setField(m.id, "focal_x", Math.round(((ev.clientX - r.left) / r.width) * 100) / 100);
                        setField(m.id, "focal_y", Math.round(((ev.clientY - r.top) / r.height) * 100) / 100);
                      }}
                      data-testid={`media-focal-${i}`}
                      className="relative block w-full border border-paper-line"
                    >
                      <img
                        src={adminMediaUrl(m.id, "md")}
                        alt={m.alt || m.title}
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                        style={{ objectPosition: `${fx * 100}% ${fy * 100}%` }}
                      />
                      <span
                        className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 border-2 border-signal"
                        style={{ left: `${fx * 100}%`, top: `${fy * 100}%` }}
                      />
                    </button>
                    <p className="ap-stamp mt-2 flex items-center gap-1">
                      <Crosshair size={11} /> Fokus {Math.round(fx * 100)}/{Math.round(fy * 100)}
                    </p>
                    <p className="ap-stamp mt-1">
                      {m.width}×{m.height} px · {Math.round((m.size || 0) / 1024)} KB · {m.content_type}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <label className="block">
                      <span className="ap-label">Titel</span>
                      <input
                        value={e.title ?? m.title}
                        onChange={(ev) => setField(m.id, "title", ev.target.value)}
                        data-testid={`media-title-${i}`}
                        className="ap-field"
                      />
                    </label>
                    <label className="block">
                      <span className="ap-label">Alt-Text (für Google und Screenreader)</span>
                      <input
                        value={e.alt ?? m.alt}
                        onChange={(ev) => setField(m.id, "alt", ev.target.value)}
                        data-testid={`media-alt-${i}`}
                        className="ap-field"
                      />
                    </label>

                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => patch(m)} className="ap-btn-solid" data-testid={`media-save-${i}`}>
                        <Save size={15} /> Speichern
                      </button>
                      <label className="ap-btn-ghost cursor-pointer" data-testid={`media-replace-label-${i}`}>
                        <RefreshCw size={15} /> Bild ersetzen
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                          onChange={(ev) => replace(m.id, ev.target.files?.[0])}
                          data-testid={`media-replace-${i}`}
                          className="sr-only"
                        />
                      </label>
                      <button type="button" onClick={() => showUsage(m.id)} className="ap-btn-ghost" data-testid={`media-usage-${i}`}>
                        Verwendung prüfen
                      </button>
                      <button type="button" onClick={() => setConfirm(m.id)} className="ap-btn-ghost" data-testid={`media-delete-${i}`}>
                        <Trash2 size={15} /> Löschen
                      </button>
                    </div>

                    {usage[m.id] ? (
                      <p className="ap-body text-charcoal" data-testid={`media-usage-result-${i}`}>
                        {usage[m.id].length
                          ? "Verwendet in: " + usage[m.id].map((u) => `${u.area}${u.title ? ` (${u.title})` : ""}`).join(", ")
                          : "Wird derzeit nirgends verwendet."}
                      </p>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-none border-paper-line bg-paper">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase">Bild wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription className="ap-body text-charcoal">
              Das Bild wird aus der Bibliothek entfernt. Bilder, die noch auf der Website verwendet
              werden, können nicht gelöscht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="media-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-none bg-ink" data-testid="media-delete-confirm">
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toast msg={msg} />
    </div>
  );
}
